import { type FC, useState, useCallback, type ChangeEventHandler, type ReactNode } from 'react';
import { useRelayEnvironment } from 'react-relay';

import { AddressAutocomplete } from 'dibs-elements/exports/AddressAutocomplete';
import { Spinner } from 'dibs-elements/exports/Spinner';
import useAddressAutocomplete, {
    type Suggestion,
} from 'dibs-address-form/exports/useAddressAutocomplete';

const FIELDS = {
    LINE_1: 'line1',
    LINE_2: 'line2',
    CITY: 'city',
    STATE: 'state',
    ZIP_CODE: 'zipCode',
    COUNTRY: 'country',
} as const;

export const addressFields = [
    FIELDS.LINE_1,
    FIELDS.LINE_2,
    FIELDS.CITY,
    FIELDS.STATE,
    FIELDS.ZIP_CODE,
    FIELDS.COUNTRY,
] as const;

export type AddressField = (typeof addressFields)[number];

export type Address = {
    [x in AddressField]: string;
};

type Props = {
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onSelect: (value: Address) => void;
    size?: 'small' | 'medium' | 'large';
    placeholder?: string;
    errorMessage?: ReactNode;
    disabled?: boolean;
    label?: string;
    onBlur?: () => void;
};

export const StreetAddressAutocomplete: FC<Props> = ({
    value,
    label,
    placeholder,
    disabled,
    errorMessage,
    size = 'medium',
    onChange,
    onSelect,
    onBlur,
}) => {
    const environment = useRelayEnvironment();
    const [isLoading, setIsLoading] = useState(false);

    const { suggestions, getAddressDetails } = useAddressAutocomplete({
        input: value,
        environment,
        placeType: 'geocode',
    });

    const handleSelect = useCallback(
        async (selected?: Suggestion | null): Promise<void> => {
            const { addressLine } = selected || {};
            setIsLoading(true);

            const addressDetails = await getAddressDetails(selected ?? null);

            const { country, city, stateOrRegion, zipCode } = addressDetails || {};

            setIsLoading(false);

            onSelect({
                line1: addressLine || '',
                line2: '',
                country: country || '',
                city: city || '',
                state: stateOrRegion || '',
                zipCode: zipCode || '',
            });
        },
        [getAddressDetails, onSelect]
    );

    return (
        <AddressAutocomplete<Suggestion>
            dataTn="address-input-autocomplete"
            name="seller-profile-address-input-autocomplete"
            size={size}
            options={suggestions || []}
            onSelect={handleSelect}
            rightDecorator={isLoading && <Spinner size="tiny" />}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            errorMessage={errorMessage}
            hasAnimatedPlaceholder
            label={label}
            onBlur={onBlur}
        />
    );
};
